/**
 * @param {HTMLElement} footerTransition
 * @returns {HTMLElement | null}
 */
function getFooterTransitionTextureElement(footerTransition) {
  const texture = footerTransition.querySelector('.footer-transition__texture');
  return texture instanceof HTMLElement ? texture : null;
}

/**
 * Footer transitions should match the lowest color surface in the previous section,
 * which is usually the element visually touching the seam.
 *
 * @param {Element | null} section
 * @returns {HTMLElement | null}
 */
function findFooterTransitionCarrier(section) {
  if (!(section instanceof HTMLElement)) return null;

  const directCandidates = [];

  if (section.className.includes('color-')) {
    directCandidates.push(section);
  }

  for (const child of section.children) {
    if (!(child instanceof HTMLElement)) continue;
    if (
      child.matches('.section-background[class*="color-"], .section[class*="color-"], [class*="color-"]')
    ) {
      directCandidates.push(child);
    }
  }

  for (const candidate of directCandidates) {
    const fill = getComputedStyle(candidate).getPropertyValue('--section-transition-fill').trim();
    if (fill) return candidate;
  }

  const fallbackCandidates = [
    ...section.querySelectorAll('.section-background[class*="color-"], .section[class*="color-"], [class*="color-"]'),
  ];

  for (let index = fallbackCandidates.length - 1; index >= 0; index -= 1) {
    const candidate = fallbackCandidates[index];
    if (!(candidate instanceof HTMLElement)) continue;

    const fill = getComputedStyle(candidate).getPropertyValue('--section-transition-fill').trim();
    if (fill) return candidate;
  }

  return null;
}

/**
 * @param {HTMLElement} footerTransition
 * @returns {HTMLElement | null}
 */
function findPreviousSectionCarrier(footerTransition) {
  let previousSection = footerTransition.closest('.shopify-section');

  while (previousSection instanceof HTMLElement) {
    previousSection = getPreviousShopifySection(previousSection);
    if (!(previousSection instanceof HTMLElement)) break;

    const carrier = findFooterTransitionCarrier(previousSection);
    if (carrier) return carrier;
  }

  return null;
}

/**
 * @param {HTMLElement} currentSection
 * @returns {HTMLElement | null}
 */
function getPreviousShopifySection(currentSection) {
  let cursor = currentSection;

  while (cursor instanceof HTMLElement) {
    let sibling = cursor.previousElementSibling;

    while (sibling instanceof HTMLElement) {
      if (sibling.classList.contains('shopify-section')) return sibling;

      const nestedSections = sibling.querySelectorAll('.shopify-section');
      if (nestedSections.length > 0) {
        const lastNestedSection = nestedSections[nestedSections.length - 1];
        if (lastNestedSection instanceof HTMLElement) return lastNestedSection;
      }

      sibling = sibling.previousElementSibling;
    }

    cursor = cursor.parentElement;
  }

  return null;
}

/**
 * @param {HTMLElement} footerTransition
 */
export function updateFooterTransitionTexture(footerTransition) {
  const texture = getFooterTransitionTextureElement(footerTransition);
  if (!texture) return;

  const carrier = findPreviousSectionCarrier(footerTransition);
  const transitionFill = carrier ? getComputedStyle(carrier).getPropertyValue('--section-transition-fill').trim() : '';
  const transitionBackground = carrier
    ? getComputedStyle(carrier).getPropertyValue('--section-transition-background').trim()
    : '';

  if (!transitionFill) {
    footerTransition.style.removeProperty('--footer-transition-previous-color');
    footerTransition.style.removeProperty('--footer-transition-previous-background');
    footerTransition.style.removeProperty('background');
    texture.hidden = true;
    return;
  }

  footerTransition.style.setProperty('--footer-transition-previous-color', transitionFill);
  footerTransition.style.setProperty(
    '--footer-transition-previous-background',
    transitionBackground || transitionFill,
  );
  footerTransition.style.background = transitionBackground || transitionFill;
  texture.hidden = false;
}

/**
 * @param {ParentNode} [root]
 */
export function updateAllFooterTransitions(root = document) {
  const footerTransitions = root.querySelectorAll('.footer-transition');

  footerTransitions.forEach((footerTransition) => {
    if (footerTransition instanceof HTMLElement) updateFooterTransitionTexture(footerTransition);
  });
}

function initFooterTextureTransitions() {
  updateAllFooterTransitions();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFooterTextureTransitions, { once: true });
} else {
  initFooterTextureTransitions();
}
